/**
 * OrderForm — the sticky horizontal Manual-Trading order form.
 *
 * Owns 3 columns:
 *   A. Symbol chips + text input, Volume input + lot chips, and (in Pending
 *      mode) the entry price + order-type chips (buy_limit / sell_limit /
 *      buy_stop / sell_stop).
 *   B. TP/SL block (delegated to TpSlBlock — Price ↔ $-distance dual mode).
 *   C. Trade-impact preview + primary action buttons (BUY / SELL / PLACE
 *      PENDING / STOP CURRENT SYMBOL).
 *
 * State stays in the parent (Meta5ManualTrading) so unrelated pieces (order
 * results section, sticky positioning, side-effects) don't have to move.
 * We accept the state atoms via props — pure presentational + click routing.
 */
import {
  Zap, Loader2, Target, TrendingUp, TrendingDown, PowerOff,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { COMMON_SYMBOLS } from './tradingHelpers';
import TpSlBlock from './TpSlBlock';
import TradeImpactPreview from './TradeImpactPreview';

export default function OrderForm({
  order, setOrder,
  livePrice,
  isAr,
  placeOrder,
  placing,
  closingAll,
  closeAll,
  connectedIds,
}) {
  return (
    <div
      className="sticky top-4 z-30 rounded-2xl border border-[#d4af37]/25 bg-[#0a0a0b]/95 backdrop-blur-xl p-4 shadow-2xl shadow-black/60 ring-1 ring-white/[0.03]"
      data-testid="manual-order-form"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-[#d4af37]" />
          <h3 className="text-sm font-bold">
            {isAr ? 'أمر موحّد على كل الحسابات' : 'Unified order — all accounts'}
          </h3>
        </div>
        <div className="flex items-center gap-1.5 p-1 rounded-lg bg-black/40 border border-white/10">
          <button
            onClick={() => setOrder({ ...order, mode: 'market', entryPrice: '' })}
            className={`px-3 py-1 rounded text-[11px] font-bold transition ${
              order.mode === 'market' ? 'bg-[#d4af37] text-black' : 'text-white/60 hover:text-white'
            }`}
            data-testid="mode-market"
          >
            {isAr ? 'فوري (Market)' : 'Market'}
          </button>
          <button
            onClick={() => setOrder({
              ...order,
              mode: 'pending',
              // Guarantee a valid pending orderType so the buttons don't
              // land on an invalid state after mode switch.
              orderType: order.orderType || 'buy_limit',
            })}
            className={`px-3 py-1 rounded text-[11px] font-bold transition ${
              order.mode === 'pending' ? 'bg-[#d4af37] text-black' : 'text-white/60 hover:text-white'
            }`}
            data-testid="mode-pending"
          >
            {isAr ? 'معلّق (Pending)' : 'Pending'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Column A: Symbol + Volume + (Entry when Pending) + Order Type */}
        <div className="lg:col-span-4 space-y-2.5">
          {order.mode === 'pending' && (
            <div className="grid grid-cols-4 gap-1">
              {[
                { id: 'buy_limit', ar: 'شراء ليمِت', en: 'Buy Limit', c: '#22c55e' },
                { id: 'sell_limit', ar: 'بيع ليمِت', en: 'Sell Limit', c: '#ef4444' },
                { id: 'buy_stop', ar: 'شراء ستوب', en: 'Buy Stop', c: '#10b981' },
                { id: 'sell_stop', ar: 'بيع ستوب', en: 'Sell Stop', c: '#f97316' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setOrder({ ...order, orderType: t.id })}
                  className={`px-2 py-1.5 rounded text-[10px] font-bold border transition ${
                    order.orderType === t.id ? 'text-black' : 'text-white/60 hover:text-white bg-white/[0.03] border-white/10'
                  }`}
                  style={order.orderType === t.id ? { background: t.c, borderColor: t.c } : {}}
                  data-testid={`order-type-${t.id}`}
                >
                  {isAr ? t.ar : t.en}
                </button>
              ))}
            </div>
          )}
          <div>
            <Label className="text-[10px] text-white/50 mb-1 block">{isAr ? 'الرمز' : 'Symbol'}</Label>
            <div className="flex gap-1 flex-wrap mb-1.5">
              {COMMON_SYMBOLS.map((s) => (
                <button
                  key={s}
                  onClick={() => setOrder({ ...order, symbol: s })}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition ${
                    order.symbol === s ? 'bg-[#d4af37]/20 border-[#d4af37]/50 text-[#d4af37]' : 'bg-white/[0.03] border-white/10 text-white/60 hover:text-white'
                  }`}
                  data-testid={`sym-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <Input
              value={order.symbol}
              onChange={(e) => setOrder({ ...order, symbol: e.target.value.toUpperCase() })}
              className="bg-black/40 border-white/10 uppercase font-mono h-8 text-xs"
              data-testid="order-symbol"
            />
          </div>
          {order.mode === 'pending' && (
            <div>
              <Label className="text-[10px] text-white/50 mb-1 block">{isAr ? 'سعر الدخول (Entry)' : 'Entry price'}</Label>
              <Input
                type="number"
                value={order.entryPrice}
                onChange={(e) => setOrder({ ...order, entryPrice: e.target.value })}
                placeholder={isAr ? 'مثال: 3450.00' : 'e.g. 3450.00'}
                className="bg-black/40 border-white/10 text-white h-8 text-xs"
                data-testid="order-entry"
              />
            </div>
          )}
          <div>
            <Label className="text-[10px] text-white/50 mb-1 block">{isAr ? 'حجم اللوت (لكل حساب)' : 'Lot (per account)'}</Label>
            <Input
              type="number"
              value={order.volume}
              onChange={(e) => setOrder({ ...order, volume: e.target.value })}
              className="bg-black/40 border-white/10 text-white h-8 text-xs"
              data-testid="order-volume"
            />
            <div className="flex gap-1 mt-1">
              {['0.01', '0.05', '0.10', '0.20', '0.50', '1.00'].map((v) => (
                <button
                  key={v}
                  onClick={() => setOrder({ ...order, volume: v })}
                  className={`flex-1 px-1 py-0.5 rounded text-[10px] border transition ${
                    order.volume === v ? 'bg-[#d4af37]/20 border-[#d4af37]/50 text-[#d4af37]' : 'bg-white/[0.03] border-white/10 text-white/50 hover:text-white'
                  }`}
                  data-testid={`lot-${v}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Column B: SL + TP */}
        <div className="lg:col-span-4">
          <TpSlBlock order={order} setOrder={setOrder} livePrice={livePrice} isAr={isAr} />
        </div>

        {/* Column C: Impact + Big Action Buttons */}
        <div className="lg:col-span-4 flex flex-col gap-2">
          <TradeImpactPreview
            order={order}
            connectedCount={connectedIds.length}
            livePrice={livePrice}
            isAr={isAr}
          />
          {order.mode === 'market' ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => placeOrder('buy')}
                disabled={placing || connectedIds.length === 0}
                className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-500/90 disabled:opacity-40 text-black font-black transition text-sm"
                data-testid="order-buy"
              >
                {placing
                  ? <Loader2 className="h-5 w-5 animate-spin" />
                  : <><TrendingUp className="h-4 w-4" />{isAr ? 'شراء الآن' : 'BUY NOW'}</>}
              </button>
              <button
                onClick={() => placeOrder('sell')}
                disabled={placing || connectedIds.length === 0}
                className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-red-500 hover:bg-red-500/90 disabled:opacity-40 text-white font-black transition text-sm"
                data-testid="order-sell"
              >
                {placing
                  ? <Loader2 className="h-5 w-5 animate-spin" />
                  : <><TrendingDown className="h-4 w-4" />{isAr ? 'بيع الآن' : 'SELL NOW'}</>}
              </button>
            </div>
          ) : (
            <button
              onClick={() => placeOrder('buy')}
              disabled={placing || connectedIds.length === 0}
              className={`w-full flex items-center justify-center gap-1.5 py-3 rounded-xl disabled:opacity-40 font-black transition text-sm ${
                order.orderType.startsWith('buy')
                  ? 'bg-emerald-500 hover:bg-emerald-500/90 text-black'
                  : 'bg-red-500 hover:bg-red-500/90 text-white'
              }`}
              data-testid="order-pending-place"
            >
              {placing
                ? <Loader2 className="h-5 w-5 animate-spin" />
                : <><Target className="h-4 w-4" />{isAr ? 'إرسال أمر معلّق' : 'PLACE PENDING'}</>}
            </button>
          )}
          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={closeAll}
              disabled={closingAll || connectedIds.length === 0}
              className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-500/15 border border-red-500/40 hover:bg-red-500/25 text-red-300 text-[11px] font-bold disabled:opacity-40"
              data-testid="order-close-all"
              title={isAr ? 'إغلاق فوري لصفقات الرمز الحالي' : 'Instant close all for this symbol'}
            >
              {closingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5" />}
              {isAr ? `إيقاف صفقات ${order.symbol}` : `STOP ${order.symbol}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
